import { addModuleToClient, getModulesByClient, removeModuleFromClient, type ClientModule } from "@/api/client.service";
import { getModules } from "@/api/module.service";
import { useNotification } from "@/context/NotificationContext";
import { Business } from "@mui/icons-material"
import { Autocomplete, Box, Button, FormHelperText, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import _SimpleEditor from "react-simple-code-editor";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Editor: typeof _SimpleEditor = (_SimpleEditor as any).default ?? _SimpleEditor;
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-json";
import "prismjs/themes/prism.css";

export function ClientsForm({ values, setValues, systems }: { values: any, setValues: any, systems: { label: string, id: string }[] }) {
  const navigate = useNavigate();
  const [modules, setModules] = useState<{ label: string, id: string }[]>([]);
  const { notify } = useNotification();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [paramsRaw, setParamsRaw] = useState<string>(
    values.params ? JSON.stringify(values.params, null, 2) : ""
  );

  const handleParamsChange = (code: string) => {
    setParamsRaw(code);
    try {
      const parsed = code.trim() ? JSON.parse(code) : null;
      setValues((prev: any) => ({ ...prev, params: parsed }));
      setErrors(prev => ({ ...prev, params: "" }));
    } catch {
      setErrors(prev => ({ ...prev, params: "JSON inválido" }));
    }
  };

  // Field-specific validators (called on blur)
  const validateName = () => {
    if (!values.name?.trim()) {
      setErrors(prev => ({ ...prev, name: "Nome é obrigatório" }));
    } else {
      setErrors(prev => ({ ...prev, name: "" }));
    }
  };

  const validateEmail = () => {
    if (!values.email?.trim()) {
      setErrors(prev => ({ ...prev, email: "Email é obrigatório" }));
    } else {
      setErrors(prev => ({ ...prev, email: "" }));
    }
  };

  const validateSystemId = () => {
    if (!values.system_id) {
      setErrors(prev => ({ ...prev, system_id: "Sistema é obrigatório" }));
    } else {
      setErrors(prev => ({ ...prev, system_id: "" }));
    }
  };

  const validateModules = () => {
    if (!values.modules) {
      setErrors(prev => ({ ...prev, modules: "Selecione ao menos um módulo" }));
    } else {
      setErrors(prev => ({ ...prev, modules: "" }));
    }
  };

  const debouncedSave = (nextModules?: { label: string, id: string }[]) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      handleSave(nextModules);
    }, 1000);
  };

  const originalModuleIds = useRef<string[]>([]);

  const loadData = async () => {
    try {
      const returnedModules = await getModules();

      setModules(returnedModules.map(m => ({ label: m.label, id: m.id })));
    } catch (error) {
      notify("Erro ao buscar dados. Tente novamente.", "error")
    }
  };

  const loadClientData = async () => {
    if (!values.id) return;

    try {
      const existingClientModules = await getModulesByClient(values.id);

      const moduleIds = existingClientModules.map(cm => cm.module_id);
      originalModuleIds.current = moduleIds;

      setValues((prev: any) => ({ ...prev, modules: moduleIds }));
    } catch (error) {
      notify("Erro ao buscar dados do cliente. Tente novamente.", "error")
    }
  }

  useEffect(() => {
    loadClientData();
  }, [values.id]);

  useEffect(() => {
    loadData();
  }, []);

  const handleSave = async (nextModules?: { label: string, id: string }[]) => {
    const currentModuleIds: string[] = nextModules?.map(m => m.id) || [];

    const toAdd = currentModuleIds.filter(id => !originalModuleIds.current.includes(id));
    const toRemove = originalModuleIds.current.filter(id => !currentModuleIds.includes(id));

    try {
      await Promise.all([
        ...toAdd.map(moduleId => addModuleToClient({
          client_id: values.id,
          module_id: moduleId
        })),
        ...toRemove.map(moduleId => removeModuleFromClient(values.id, moduleId))
      ]);

      originalModuleIds.current = currentModuleIds;
    } catch (err) {
      console.error("Save failed", err);
    }
  };

  return (
    <>
      <TextField
        label="Nome"
        value={values.name || ""}
        onChange={(e) =>
          setValues({ ...values, name: e.target.value })
        }
        onBlur={validateName}
        error={!!errors.name}
        helperText={errors.name}
        required
        fullWidth
      />

      <TextField
        label="Nome de grupo"
        value={values.group_name || ""}
        onChange={(e) =>
          setValues({ ...values, group_name: e.target.value })
        }
        fullWidth
      />

      <TextField
        label="Email"
        value={values.email || ""}
        onChange={(e) =>
          setValues({ ...values, email: e.target.value })
        }
        onBlur={validateEmail}
        error={!!errors.email}
        helperText={errors.email}
        required
        fullWidth
      />

      <TextField
        label="URL do Sistema"
        value={values.system_url || ""}
        onChange={(e) =>
          setValues({ ...values, system_url: e.target.value || null })
        }
        fullWidth
      />

      <Autocomplete
        disablePortal
        options={systems}
        value={systems.find((s) => s.id === values.system_id) || null}

        onChange={(_, newValue) => {
          setValues({
            ...values,
            system_id: newValue ? newValue.id : null
          });
        }}

        onBlur={validateSystemId}
        getOptionLabel={(option) => option.label || ""}

        isOptionEqualToValue={(option, value) => option.id === value.id}

        renderInput={(params) => (
          <TextField {...params} label="Sistema" error={!!errors.system_id} helperText={errors.system_id} />
        )}
        fullWidth
      />

      <Autocomplete
        multiple
        options={modules}
        value={modules.filter(m => values.modules?.includes(m.id))}
        onChange={(_, newValue) => {
          const nextModuleIds = newValue.map(m => m.id);
          setValues({ ...values, modules: nextModuleIds });
          debouncedSave(newValue);
        }}

        onBlur={validateModules}
        getOptionLabel={(option) => option.label}

        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => <TextField {...params} label="Módulos" error={!!errors.modules} helperText={errors.modules} />}
        fullWidth
      />

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
          Parâmetros (JSON)
        </Typography>
        <Box
          sx={{
            border: errors.params ? "1px solid" : "1px solid",
            borderColor: errors.params ? "error.main" : "divider",
            borderRadius: 1,
            fontFamily: "monospace",
            fontSize: 13,
            minHeight: 120,
            "& textarea": { outline: "none" },
          }}
        >
          <Editor
            value={paramsRaw}
            onValueChange={handleParamsChange}
            highlight={(code) => highlight(code, languages.json, "json")}
            padding={12}
            style={{ minHeight: 120 }}
            tabSize={2}
            insertSpaces
          />
        </Box>
        {errors.params && (
          <FormHelperText error>{errors.params}</FormHelperText>
        )}
      </Box>

      {
        values && values.id ?
          <Button
            variant="contained"
            startIcon={<Business />}
            fullWidth
            onClick={() => {
              navigate(`/empresas/${values.id}`)
            }}
          >
            Editar Empresas
          </Button> :
          <></>
      }
    </>
  );
}
